/***********************************
 *  Grade3MathLesson.js  (v2)
 *  Matches the Grade2 structure
 ***********************************/

/* ----------------------------------
   1. Import Statements
---------------------------------- */
import React, { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import "./Grade3MathLesson.css";
import draw3DShapeOnCanvas from "../MathTools/draw3DShape.js";
import drawGraphOnCanvas from "../MathTools/drawGraph.js";
import ProgressBar from "../MathPagesStructure/ProgressBar.js";
import LessonUI from "../MathPagesStructure/LessonUI.js";
import { StoryModeQuiz, QuizResultScreen, FinalQuizUI } from "../MathPagesStructure/StoryModeStructure.js";
import { InteractiveGraphSection, drawShapeOnCanvas,} from "../MathTools/ElementaryMathHelperTool";
import { mathChapterPages } from "../MathLessonPools";
import { auth, db } from "../../firebase";
import { doc, runTransaction, serverTimestamp } from "firebase/firestore";

/* ----------------------------------
   3. Main Component: Grade3MathLesson
---------------------------------- */
export default function Grade3MathLesson({onAwardTokens}) {
  /* -------------------------------------------------
     A. Drawing Refs and Local State for Demos
  ------------------------------------------------- */
  const graphCanvasRef = useRef(null);
  const canvasRef = useRef(null);
  const canvas3DRef = useRef(null);

  // 2D Shapes
  const [shapeIdx, setShapeIdx] = useState(0);
  const shapeNames = ["Triangle", "Square", "Circle", "Pentagon", "Hexagon"];
  const cycleShape = () => setShapeIdx((i) => (i + 1) % shapeNames.length);

  // 3D Shapes
  const [solidIdx, setSolidIdx] = useState(0);
  const solidNames = [
    "Cube",
    "Rectangular Prism",
    "Pyramid",
    "Triangular Prism",
    "Cylinder",
    "Sphere"
  ];
  const cycleSolid = () => setSolidIdx((i) => (i + 1) % solidNames.length);

  // Graph data is memoized so its reference does not change on every render
  const sampleGraphData = useMemo(
    () => [
      { label: "Dog", value: 4 },
      { label: "Cat", value: 3 },
      { label: "Fish", value: 2 },
      { label: "Bird", value: 1 }
    ],
    []
  );

  /* -------------------------------------------------
     B. Component State for Chapters, Quizzes, etc.
  ------------------------------------------------- */
  const [chapter, setChapter] = useState(1);
  const [chapDone, setChapDone] = useState(false);
  const [pageIdx, setPageIdx] = useState(0);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [time, setTime] = useState(300);

    // -------------- NEW FEATURE #1 --------------
  // Track if user completed before + localStorage
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);
  useEffect(() => {
    const done = localStorage.getItem("grade3MathComplete");
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true); // means all chapters are done
    }
  }, []);

  useEffect(() => {
    if (chapDone) {
      localStorage.setItem("grade3MathComplete", "true");
    }
  }, [chapDone]);

    // -------------- NEW FEATURE #2 --------------
  // For awarding tokens if final quiz >=80%
  const hasAwardedTokens = useRef(false);

  // Also track user from localStorage to refresh token count, etc.
  const [user, setUser] = useState(null);
  useEffect(() => {
    const logged = JSON.parse(localStorage.getItem("loggedInUser"));
    if (logged) setUser(logged);
  }, []);


  /* -------------------------------------------------
     C. Static Data: ChapterPages, Quiz Generator, and Topic Map
     (Memoized to avoid re-creation on every render)
  ------------------------------------------------- */

  // Quiz generator functions and topic map
  function rand(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
  }
  function genQuestion(forcedTopic = null) {
    const topics = [
      "numberOps",
      "patternsAlgebra",
      "measurement",
      "geometry",
      "dataProbability",
      "financialLit",
    ];
    const topic = forcedTopic || rand(topics);
    let q = "",
      a,
      opts = [];
    /* 1️⃣  Number Ops */
    if (topic === "numberOps") {
      const sub = rand(["add", "sub", "mul", "div", "frac"]);
      if (sub === "add") {
        const n1 = Math.floor(Math.random() * 500) + 100;
        const n2 = Math.floor(Math.random() * 400) + 50;
        a = n1 + n2;
        q =
          `${String(n1).padStart(4, " ")}` +
          `\n+${String(n2).padStart(3, " ")}` +
          `\n────`;
      } else if (sub === "sub") {
        let n1 = Math.floor(Math.random() * 900) + 100;
        let n2 = Math.floor(Math.random() * n1);
        a = n1 - n2;
        q =
          `${String(n1).padStart(4, " ")}` +
          `\n−${String(n2).padStart(3, " ")}` +
          `\n────`;
      } else if (sub === "mul") {
        const r = rand([[4, 5], [6, 3], [8, 4]]);
        q = `${r[0]} × ${r[1]} = ?`;
        a = r[0] * r[1];
      } else if (sub === "div") {
        const d = rand([[12, 4], [20, 5], [18, 3]]);
        q = `${d[0]} ÷ ${d[1]} = ?`;
        a = d[0] / d[1];
      } else {
        const den = rand([2, 3, 4]);
        const mult = Math.floor(Math.random() * 6) + 2;
        const total = den * mult;
        q = `What is 1/${den} of ${total}?`;
        a = total / den;
      }
      opts = [a, a + rand([1, 2]), Math.max(1, a - rand([1, 2]))];
    }
    /* 2️⃣  Patterns & Algebra */
    else if (topic === "patternsAlgebra") {
      const subtype = rand(["pattern", "unknown", "inequality"]);
      if (subtype === "pattern") {
        const start = rand([3, 4, 5, 10, 12]);
        const step = rand([2, 3, 5]);
        const dir = rand(["up", "down"]);
        const seq = Array.from({ length: 4 }, (_, i) =>
          dir === "up" ? start + step * i : start - step * i
        );
        q = `What comes next? ${seq.join(", ")}, ___`;
        a = dir === "up" ? seq[3] + step : seq[3] - step;
        opts = [a, a + step, a - step];
      } else if (subtype === "unknown") {
        const num1 = rand([3, 4, 5, 6]);
        const num2 = rand([6, 7, 8, 9]);
        const result = num1 + num2;
        const missing = rand(["left", "right"]);
        if (missing === "left") {
          q = `___ + ${num2} = ${result}`;
          a = num1;
        } else {
          q = `${num1} + ___ = ${result}`;
          a = num2;
        }
        opts = [a, a + 1, Math.max(1, a - 1)];
      } else {
        const n1 = rand([4, 5, 6]);
        const n2 = rand([3, 4, 5]);
        const sum = n1 + n2;
        const isCorrect = rand([true, false]);
        const shown = isCorrect ? sum : sum + rand([1, -1]);
        q = `${n1} + ${n2} = ${shown} → Is this correct?`;
        a = isCorrect ? "Yes" : "No";
        opts = ["Yes", "No"];
      }
    }
    /* 3️⃣  Measurement */
    else if (topic === "measurement") {
      const sub = rand(["convert", "perimeter", "elapsed", "compare", "estimate"]);
      if (sub === "convert") {
        const conversions = [
          ["m", "cm", 100],
          ["km", "m", 1000],
          ["L", "mL", 1000],
          ["kg", "g", 1000],
          ["min", "sec", 60]
        ];
        const [from, to, factor] = rand(conversions);
        const value = rand([1, 2, 3, 4, 5]);
        q = `How many ${to} in ${value} ${from}?`;
        a = value * factor;
        opts = [a, a + rand([10, 50]), Math.max(1, a - rand([10, 50]))];
      } else if (sub === "perimeter") {
        const l = rand([2, 3, 4, 5, 6, 7]);
        const w = rand([1, 2, 3, 4, 5]);
        q = `A rectangle is ${l} cm long and ${w} cm wide. What is its perimeter?`;
        a = 2 * (l + w);
        opts = [a, a + 2, a - 2];
      } else if (sub === "elapsed") {
        const startH = rand([8, 9, 10, 11]);
        const duration = rand([30, 45, 60, 90]);
        const endH = startH + Math.floor(duration / 60);
        const endM = duration % 60;
        q = `A class starts at ${startH}:00 and ends at ${endH}:${String(
          endM
        ).padStart(2, "0")}. How many minutes long is it?`;
        a = duration;
        opts = [a, a + 15, Math.max(1, a - 15)];
      } else if (sub === "compare") {
        const items = [
          ["a pencil", 17],
          ["your arm", 60],
          ["a car", 400]
        ];
        const [item, length] = rand(items);
        const guess = rand([10, 20, 30, 50, 100]);
        q = `Is ${item} likely longer than ${guess} cm?`;
        a = length > guess ? "Yes" : "No";
        opts = ["Yes", "No"];
      } else if (sub === "estimate") {
        const object = rand([
          ["a teacup", "mL", 250],
          ["a bathtub", "L", 150],
          ["a watermelon", "kg", 5],
          ["a feather", "g", 1]
        ]);
        const [name, unit, approx] = object;
        q = `About how many ${unit} does ${name} weigh/hold?`;
        a = approx;
        opts = [approx, approx + rand([50, 100]), Math.max(1, approx - rand([50, 100]))];
      }
    }
    /* 4️⃣  Geometry */
    else if (topic === "geometry") {
      const sub = rand(["sides", "faces", "angles", "transform", "parallel"]);
      if (sub === "sides") {
        const shapes = [
          ["triangle", 3],
          ["square", 4],
          ["pentagon", 5],
          ["hexagon", 6],
          ["octagon", 8]
        ];
        const [shape, sides] = rand(shapes);
        q = `How many sides does a ${shape} have?`;
        a = sides;
        opts = [a, a + 1, a - 1];
      } else if (sub === "faces") {
        const pool = [
          { type: "face", shape: "cube", faces: 6 },
          { type: "face", shape: "rectangular prism", faces: 6 },
          { type: "face", shape: "sphere", faces: 1 },
          { type: "angle", q: "Is a square’s corner a right angle?", answer: "Yes" },
          { type: "angle", q: "Is a right angle 90°?", answer: "Yes" }
        ];
        const chosen = rand(pool);
        if (chosen.type === "face") {
          q = `How many faces does a ${chosen.shape} have?`;
          a = chosen.faces;
          opts = [a, a + 1, a - 1];
        } else {
          q = chosen.q;
          a = chosen.answer;
          opts = ["Yes", "No"];
        }
      } else if (sub === "angles") {
        const pool = [
          ["Is a square’s corner a right angle?", "Yes"],
          ["Is a right angle 90°?", "Yes"]
        ];
        [q, a] = rand(pool);
        opts = ["Yes", "No"];
      } else {
        const pool = [
          ["Do parallel lines ever meet?", "No"],
          ["Do perpendicular lines form right angles?", "Yes"]
        ];
        [q, a] = rand(pool);
        opts = ["Yes", "No"];
      }
    }
    /* 5️⃣  Data & Probability */
else if (topic === "dataProbability") {
  // random scenario for probability terms
  const sub = rand(["vocab", "votes", "realLife", "compare", "graph"]);

  let correctAnswer = "";
  
  if (sub === "vocab") {
    const pool = [
      ["Which word means 0 % chance?", "impossible"],
      ["Which word means 100 % chance?", "certain"],
      ["Which word means more than 50 % chance?", "likely"],
      ["Which word means less than 50 % chance?", "unlikely"]
    ];
    // Because we destructure [q, correctAnswer], rename second param to correctAnswer.
    [q, correctAnswer] = rand(pool);

    // Now pick distractors
    opts = ["certain", "likely", "unlikely", "impossible"]
      .filter(opt => opt !== correctAnswer);
    // Then place correct answer in front, plus 2 random distractors
    opts = [correctAnswer, ...rand(opts, 2)];

  } else if (sub === "votes") {
    const votes = rand([
      { cat: 7, dog: 3 },
      { apple: 6, banana: 4 },
      { soccer: 5, basketball: 8 },
      { red: 2, blue: 9 }
    ]);
    const keys = Object.keys(votes);

    // rename from `a` to `correctAnswer`
    correctAnswer = votes[keys[0]] > votes[keys[1]] ? keys[0] : keys[1];

    q = `Survey: ${keys[0]} (${votes[keys[0]]}) vs ${keys[1]} (${votes[keys[1]]}). Which is more popular?`;
    opts = keys; // keys array, e.g. ["cat", "dog"]
    
  } else if (sub === "realLife") {
    const pool = [
      ["What's more likely: rain or snow in July?", "rain"],
      ["What's more likely: rolling a 6 or a number less than 6 on a dice?", "less than 6"],
      [
        "What’s more likely: drawing a red heart or a blue square from a deck of red hearts?",
        "red heart"
      ]
    ];
    // again rename second param to correctAnswer
    [q, correctAnswer] = rand(pool);

    // form initial options
    opts = [correctAnswer, "snow", "rolling a 6", "blue square"]
      .filter((x, i, arr) => arr.indexOf(x) === i) // deduplicate
      .slice(0, 3);
    if (!opts.includes(correctAnswer)) {
      opts[0] = correctAnswer;
    }

  } else if (sub === "compare") {
    let red = Math.floor(Math.random() * 5) + 1;
    let blue = Math.floor(Math.random() * 5) + 1;
    // ensure no tie
    while (red === blue) {
      blue = Math.floor(Math.random() * 5) + 1;
    }

    const bigger = red > blue ? "red" : "blue";
    q = `There are ${red} red marbles and ${blue} blue marbles. Which is more likely to be picked?`;
    correctAnswer = bigger;
    opts = ["red", "blue"];

  } else {
    // sub === "graph" scenario
    const bars = rand([
      { apples: 5, oranges: 3 },
      { cats: 4, dogs: 6 },
      { bikes: 8, cars: 2 }
    ]);
    const entries = Object.entries(bars); 
    // e.g. entries: [ ["bikes",8], ["cars",2] ]
    const [k1, v1] = entries[0];
    const [k2, v2] = entries[1];

    correctAnswer = (v1 > v2) ? k1 : k2;

    q = `Bar graph: ${k1} (${v1}) vs ${k2} (${v2}). Which has more?`;
    opts = [k1, k2];
  }

  // once we’re done with subtopic logic, return question + correctAnswer + options:
  return { question: q, correctAnswer, options: opts };
}

    /* 6️⃣  Financial Literacy */
    else if (topic === "financialLit") {
      const sub = rand([0, 1, 2, 3, 4, 5]);
      if (sub === 0) {
        const have = rand([3, 4, 5, 6, 7, 8]);
        const toy = Math.floor(Math.random() * (have + 2)) + 1;
        const snack = Math.floor(Math.random() * (have + 2)) + 1;
        q = `You have $${have}. Toy costs $${toy}, snack costs $${snack}. Can you buy both?`;
        a = toy + snack <= have ? "Yes" : "No";
        opts = ["Yes", "No"];
      } else if (sub === 1) {
        const cost = rand([5, 8, 10, 12, 15]);
        const have = Math.floor(Math.random() * cost);
        q = `You want an item that costs $${cost}, but you only have $${have}. How much more do you need?`;
        a = cost - have;
        opts = [a, a + rand([1, 2]), Math.max(1, a - rand([1, 2]))];
      } else if (sub === 2) {
        const paid = rand([5, 10, 20]);
        const cost = Math.floor(Math.random() * (paid - 1)) + 1;
        q = `You pay $${paid} for an item that costs $${cost}. How much change will you get back?`;
        a = paid - cost;
        opts = [a, a + rand([1, 2]), Math.max(0, a - rand([1, 2]))];
      } else if (sub === 3) {
        const item = rand([8, 10, 12, 15, 18, 20]);
        const save = rand([1, 2, 3, 4, 5]);
        q = `An item costs $${item}. You save $${save} each week. How many weeks do you need?`;
        a = Math.ceil(item / save);
        opts = [a, a + 1, Math.max(1, a - 1)];
      } else if (sub === 4) {
        const toonies = rand([0, 1, 2, 3]);
        const loonies = rand([1, 2, 3, 4]);
        const quarters = rand([1, 2, 3]);
        const total = toonies * 2 + loonies * 1 + quarters * 0.25;
        q = `You have ${toonies} toonies, ${loonies} loonies, and ${quarters} quarters. How much do you have in total?`;
        a = parseFloat(total.toFixed(2));
        opts = [
          a,
          parseFloat((a + 0.25).toFixed(2)),
          parseFloat((a - 0.25).toFixed(2))
        ];
      } else {
        const total = rand([10, 12, 15]);
        const spent = rand([3, 4, 5, 6]);
        const spent2 = rand([1, 2, 3]);
        const totalSpent = spent + spent2;
        q = `You have $${total}. You spend $${spent} on lunch and $${spent2} on a toy. How much do you have left?`;
        a = total - totalSpent;
        opts = [a, a + 1, Math.max(0, a - 1)];
      }
      if (!(opts.length === 2 && opts.includes("Yes"))) {
        opts.sort(() => Math.random() - 0.5);
      }
    }
    if (!(opts.length === 2 && opts.includes("Yes"))) {
      opts.sort(() => Math.random() - 0.5);
    }
    return { question: q, correctAnswer: a, options: opts };
  }

  const genQuiz = (n, topic = null) =>
    Array.from({ length: n }, () => genQuestion(topic));
  const genFinal = () => {
    const used = new Set();
    const arr = [];
    while (arr.length < 50) {
      const q = genQuestion();
      const key = `${q.question}-${q.correctAnswer}`;
      if (!used.has(key)) {
        used.add(key);
        arr.push(q);
      }
    }
    return arr;
  };

  const chapterTopicMap = {
    1: "numberOps",
    2: "patternsAlgebra",
    3: "measurement",
    4: "geometry",
    5: "dataProbability",
    6: "financialLit",
  };

  /* -------------------------------------------------
     D. useEffect Hooks for Drawing and Timer
  ------------------------------------------------- */
  useEffect(() => {
    drawGraphOnCanvas(graphCanvasRef.current, sampleGraphData);
  }, [sampleGraphData]);

  const curPages = mathChapterPages[chapter];

  useEffect(() => {
    if (chapter === 4 && curPages[pageIdx]?.isShapeDemo) {
      drawShapeOnCanvas(canvasRef.current, shapeIdx);
    }
  }, [chapter, pageIdx, shapeIdx, curPages]);

  useEffect(() => {
    if (chapter === 4 && curPages[pageIdx]?.isShapeDemo) {
      draw3DShapeOnCanvas(canvas3DRef.current, solidIdx);
    }
  }, [chapter, pageIdx, solidIdx, curPages]);

useEffect(() => {
let alive = true;
if (showFinal && !fFin && time > 0) {
const t = setTimeout(() => {
if (alive) setTime(s => s - 1);
}, 1000);
return () => { alive = false; clearTimeout(t); };
} else if (time === 0) {
setFFin(true);
}
}, [showFinal, fFin, time]);

  /* -------------------------------------------------
     E. Helper Functions for Quiz and Navigation
  ------------------------------------------------- */
  const resetQuizState = () => {
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
  };

  const startChapQuiz = () => {
    const topic = chapterTopicMap[chapter];
    setQuiz(genQuiz(10, topic));
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
  };

  const handleChapAnswer = (opt) => {
    if (opt === quiz[qIdx].correctAnswer) setQScore((s) => s + 1);
    if (qIdx < quiz.length - 1) setQIdx((i) => i + 1);
    else setQuizFin(true);
  };

  const handleChapFinish = () => {
    if (qScore >= 5) {
      if (chapter < 6) {
        setChapter((c) => c + 1);
        setPageIdx(0);
        resetQuizState();
      } else {
        setChapDone(true);
      }
    } else {
      alert("Below 50 %. Review and retry!");
      resetQuizState();
      const topic = chapterTopicMap[chapter];
      const newQuiz = genQuiz(10, topic);
      setQuiz(newQuiz);
      setQIdx(0);
    }
  };

  const startFinal = () => {
    setFinalQs(genFinal());
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTime(300);
    setShowFinal(true);
  };

  const handleFinalAns = (opt) => {
    if (opt === finalQs[fIdx].correctAnswer) setFScore((s) => s + 1);
    if (fIdx < finalQs.length - 1) setFIdx((i) => i + 1);
    else setFFin(true);
  };

useEffect(() => {
  if (!fFin || hasAwardedTokens.current) return;
  (async () => {
    const passed = (fScore / finalQs.length) * 100 >= 80;
    const uid    = auth.currentUser?.uid;
    if (!uid) return;
    try {
      await runTransaction(db, async tx => {
        const ref  = doc(db, "users", uid);
        const snap = await tx.get(ref);
        const data = snap.data() || {};
        const progress = {
          ...(data.progress || {}),
          math_g3: {
            completed: true,
            finalQuizScore: fScore,
            finalQuizTotal: finalQs.length,
            lastCompleted: serverTimestamp(),
          },
        };
        const update = { progress };
        if (passed) update.coins = (data.coins ?? 0) + 1;
        tx.set(ref, update, { merge: true });
      });
      if (passed && onAwardTokens) onAwardTokens(1);
      hasAwardedTokens.current = true;
    } catch (err) {
      console.error("Save math_g3 progress failed →", err);
    }
  })();
}, [fFin, fScore, finalQs.length, onAwardTokens]);

  
    // -------------- NEW FEATURE #3 --------------
  // Jump to chapter UI
  function jumpToChapter(chNum) {
    setChapter(chNum);
    setPageIdx(0);
    resetQuizState();
    setShowFinal(false);
  }
  function jumpDirectlyToFinal() {
    startFinal();
  }

  const progress = Math.min((chapter - 1) * 20, 100);

  const ChapterUI = () => (
    <>
      {/* 1. LESSON PAGES (if no quiz started) */}
      {quiz.length === 0 && !quizFin && (
        <LessonUI
          pages={mathChapterPages[chapter]}
          currentPageIndex={pageIdx}
          onPrevPage={() => setPageIdx((p) => p - 1)}
          onNextPage={() => setPageIdx((p) => p + 1)}
          onStartQuiz={
            chapter === 1
              ? () => {
                  if (!quiz.length) startChapQuiz();
                }
              : startChapQuiz
          }
          backTo="/math"
          renderExtraSection={({ page }) => {
            // Insert any custom per-slide demo logic here:
            if (chapter === 5 && page.isGraphDemo) {
              return (
                <InteractiveGraphSection
                  graphCanvasRef={graphCanvasRef}
                  initialData={[
                    { label: "Dog", value: 4 },
                    { label: "Cat", value: 3 },
                    { label: "Fish", value: 2 },
                    { label: "Bird", value: 1 }
                  ]}
                />
              );
            }
            if (chapter === 4 && page.isShapeDemo) {
              return (
                <>
                  <div className="shape-container">
                    <canvas
                      ref={canvasRef}
                      width="200"
                      height="140"
                      className="shapes-canvas"
                    />
                    <p className="shape-name">{shapeNames[shapeIdx]}</p>
                  </div>
                  <button className="button1" onClick={cycleShape}>
                    Draw Next 2D Shape
                  </button>
                  <div className="shape-container" style={{ marginTop: 20 }}>
                    <canvas
                      ref={canvas3DRef}
                      width="200"
                      height="200"
                      className="shapes-canvas"
                    />
                    <p className="shape-name">{solidNames[solidIdx]}</p>
                  </div>
                  <button className="button1" onClick={cycleSolid}>
                    Draw Next 3D Shape
                  </button>
                </>
              );
            }
            return null;
          }}
        />
      )}
  
      {/* 2. CHAPTER QUIZ (if started and not finished) */}
      {quiz.length > 0 && !quizFin && (
        <StoryModeQuiz
          questions={quiz}
          onAnswer={handleChapAnswer}
          currentIndex={qIdx}
          score={qScore}
          user={user}
        />
      )}
  
      {/* 3. QUIZ RESULT (if finished) */}
      {quizFin && (
        <QuizResultScreen
          score={qScore}
          total={quiz.length}
          onContinue={handleChapFinish}
        />
      )}
    </>
  );

  /* -------------------------------------------------
     G. Main Render: Return the Overall UI
  ------------------------------------------------- */
  return (
    <div className="grade3-math-page">
      <div className="grade3-math-page-scale">
        {/* Progress Bar */}
        <ProgressBar progress={progress} />

        {/* If user previously completed => jump UI */}
        {hasCompletedBefore && !showFinal && quiz.length === 0 && !quizFin && (
          <div style={{ margin: "15px 0", textAlign: "center", color: "#555" }}>
            <p style={{ marginBottom: 10 }}>
              You previously completed all Grade 3 lessons.
              You can jump to any chapter or retake the final quiz for practice.
            </p>
            <div>
              {[1, 2, 3, 4, 5, 6].map((ch) => (
                <button
                  key={ch}
                  style={{ marginRight: 8 }}
                  onClick={() => jumpToChapter(ch)}
                >
                  Go to Chapter {ch}
                </button>
              ))}
              <button onClick={jumpDirectlyToFinal}>Go to Final Quiz</button>
            </div>
          </div>
        )}

        {/* Chapter Lessons or Quiz */}
        {!chapDone && !showFinal && <ChapterUI />}
        {chapDone && !showFinal && (
          <div style={{ textAlign: "center" }}>
            <h3>All Chapters Completed!</h3>
            <button className="button1" onClick={startFinal}>
              Start Final Quiz
            </button>
          </div>
        )}
        {showFinal && (
  <FinalQuizUI
    questions={finalQs}
    currentIndex={fIdx}
    score={fScore}
    timeLeft={time}
    onAnswer={handleFinalAns}
    quizFinished={fFin}
    onReturn={() => setShowFinal(false)}
    resultMsg={user && (
      <p>
        You now have <strong>{user.tokens ?? 0}</strong> tokens!
      </p>
    )}
  />
)}

        <br />
        <Link to="/math" className="grade3-math-back">
          ← Back to Math Page
        </Link>
      </div>
    </div>
  );
}
